import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CircleNotch, Headset, User as UserIcon, X, ChatCircleDots } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

/**
 * THE support ticket conversation, for the platform console — one implementation, two places.
 *
 * The support queue and the ticket detail page both need the same thread and the same composer.
 * Rather than a second rendering of the same messages (which is how two views of one fact start
 * disagreeing — Hard Rule #14), this component is the only place the console renders a support
 * thread: `SupportTicketDetailPage` embeds it, and the row-level 💬 action on the queue opens it
 * in a drawer. Both read the SAME query key (`['platform','support',id]`) and the SAME endpoints
 * (`GET /support/:id`, `POST /support/:id/replies`), so React Query dedupes them to a single
 * fetch and a reply sent from either surface updates the other.
 *
 * There is no separate "communication" API, table or inbox: communication IS the ticket.
 */

export interface SupportConversationMessage {
  id: string;
  body: string;
  internal: boolean;
  authorName: string | null;
  authorType: 'support' | 'customer';
  createdAt: string;
}

interface ConversationTicket {
  id: string;
  title: string;
  body: string;
  status: string;
  createdAt: string;
  requesterName: string | null;
  requesterEmail: string | null;
  messages: SupportConversationMessage[];
}

function fmt(s: string) {
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Shared by the detail page and the drawer so both hit ONE cache entry, never two fetches and
 * never two ways of loading the same ticket. The detail page reads a wider projection of the same
 * response, hence the type parameter.
 */
export function useSupportTicket<T = ConversationTicket>(ticketId: string | undefined) {
  return useQuery<T>({
    queryKey: ['platform', 'support', ticketId],
    queryFn: async () => {
      const res = await platformApi.get(`/support/${ticketId}`);
      return (res.data?.data ?? res.data).ticket;
    },
    enabled: !!ticketId,
  });
}

// ─── The thread ───────────────────────────────────────────────────────────────

export function SupportConversation({
  ticketId,
  showOpeningReport = false,
  autoFocusReply = false,
}: {
  ticketId: string;
  /** The drawer has no other context, so it leads with the customer's original report. */
  showOpeningReport?: boolean;
  autoFocusReply?: boolean;
}) {
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const replyRef = useRef<HTMLTextAreaElement>(null);

  const { data: ticket, isLoading } = useSupportTicket(ticketId);

  useEffect(() => {
    if (autoFocusReply && ticket) replyRef.current?.focus();
  }, [autoFocusReply, ticket]);

  const replyMutation = useMutation({
    mutationFn: () => platformApi.post(`/support/${ticketId}/replies`, { body: reply, internal }),
    onSuccess: (res) => {
      setReply('');
      const notified = (res.data?.data ?? {}).notified;
      toast.success(
        internal ? 'Internal note saved' : notified ? 'Reply sent — the customer was notified' : 'Reply saved',
      );
      // Invalidates the thread AND the queue, so the row's unanswered badge clears immediately.
      queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  if (isLoading || !ticket) {
    return (
      <div className="flex items-center justify-center py-10">
        <CircleNotch className="h-5 w-5 animate-spin text-plat-muted" />
      </div>
    );
  }

  return (
    <div>
      {showOpeningReport && (
        <div className="mb-3 rounded-lg border border-plat-border bg-plat-bg p-3">
          <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-plat-muted">
            <UserIcon className="h-3.5 w-3.5" />
            {ticket.requesterName ?? ticket.requesterEmail ?? 'Customer'}
            <span>· {fmt(ticket.createdAt)}</span>
            <span className="rounded bg-plat-neutral-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase text-plat-secondary">
              Original report
            </span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-plat-fg">{ticket.body}</p>
        </div>
      )}

      {ticket.messages.length === 0 ? (
        <p className="text-sm text-plat-muted">No replies yet.</p>
      ) : (
        <div className="space-y-3">
          {ticket.messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-lg border p-3 ${
                m.internal
                  ? 'border-plat-warning-soft bg-plat-warning-soft/20'
                  : 'border-plat-border bg-plat-bg'
              }`}
            >
              <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs font-medium text-plat-muted">
                {m.authorType === 'support'
                  ? <Headset className="h-3.5 w-3.5 text-plat-info-text" />
                  : <UserIcon className="h-3.5 w-3.5" />}
                {m.authorName ?? (m.authorType === 'support' ? 'Support' : 'Customer')}
                <span>· {fmt(m.createdAt)}</span>
                {m.internal && (
                  <span className="rounded bg-plat-warning-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase text-plat-warning-text">
                    Internal — not visible to customer
                  </span>
                )}
              </div>
              <p className="whitespace-pre-wrap text-sm text-plat-fg">{m.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <label htmlFor={`support-reply-${ticketId}`} className="sr-only">
          {internal ? 'Internal note' : 'Reply to the customer'}
        </label>
        <textarea
          id={`support-reply-${ticketId}`}
          ref={replyRef}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={4}
          placeholder={internal ? 'Internal note — the customer never sees this…' : 'Reply to the customer…'}
          className="w-full rounded-lg border border-plat-border bg-plat-bg p-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none"
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-plat-secondary">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            Internal note (not sent to the customer)
          </label>
          <button
            onClick={() => replyMutation.mutate()}
            disabled={!reply.trim() || replyMutation.isPending}
            className="rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
          >
            {replyMutation.isPending
              ? <CircleNotch className="h-4 w-4 animate-spin" />
              : internal ? 'Save note' : 'Send reply'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── The drawer the row action opens ──────────────────────────────────────────

/**
 * Opens the conversation for ONE ticket, in place, without leaving the queue. Triage is a
 * sequence of short exchanges; making an executive navigate away and back for each one is the
 * workflow friction this action exists to remove.
 */
export function SupportConversationDrawer({
  ticketId,
  title,
  requester,
  onClose,
}: {
  ticketId: string;
  title: string;
  requester: string | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-plat-scrim/60" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Conversation — ${title}`}
        className="relative flex h-full w-full max-w-xl flex-col border-l border-plat-border bg-plat-surface shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-plat-border p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">
              <ChatCircleDots className="h-3.5 w-3.5" /> Conversation
            </div>
            <h3 className="mt-1 truncate text-[15px] font-semibold text-plat-fg">{title}</h3>
            {requester && <p className="mt-0.5 truncate text-xs text-plat-muted">with {requester}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close conversation"
            className="rounded-md p-1 text-plat-muted hover:bg-plat-hover hover:text-plat-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <SupportConversation ticketId={ticketId} showOpeningReport autoFocusReply />
        </div>
      </div>
    </div>
  );
}

// ─── The row action ───────────────────────────────────────────────────────────

/**
 * Compact, row-level entry into the conversation.
 *
 * The badge is UNANSWERED, not unread — the schema has no per-message read receipt and a label
 * that claimed otherwise would be a number nobody could trust. It counts customer messages that
 * have arrived since Support's last reply (see conversationSignals in support.service.ts).
 */
export function SupportConversationButton({
  unanswered,
  messageCount,
  onClick,
  className = '',
}: {
  unanswered: number;
  messageCount: number;
  onClick: () => void;
  className?: string;
}) {
  const label = unanswered > 0
    ? `Conversation — ${unanswered} customer ${unanswered === 1 ? 'message' : 'messages'} awaiting a reply`
    : messageCount > 0
      ? `Conversation — ${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`
      : 'Conversation — no replies yet';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`relative inline-flex h-8 w-8 items-center justify-center rounded-md text-plat-muted transition-colors hover:bg-plat-hover hover:text-plat-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus ${className}`}
    >
      <ChatCircleDots className="h-[18px] w-[18px]" weight={unanswered > 0 ? 'fill' : 'regular'} />
      {unanswered > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-plat-danger-solid px-1 text-[10px] font-bold leading-none text-plat-on-accent"
        >
          {unanswered > 9 ? '9+' : unanswered}
        </span>
      )}
    </button>
  );
}
